import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_PAYMENT_SETTINGS, type PaymentSettings } from "./types";

/**
 * Reads the singleton payment_settings row. Read is unauthenticated
 * (anon policy `Anyone reads payment settings`) so guests can see
 * available methods on checkout.
 */
export function usePaymentSettings() {
  const [settings, setSettings] = useState<PaymentSettings>(DEFAULT_PAYMENT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const apply = (data: any) => {
      if (!data) return;
      setSettings({
        bank_accounts:
          (data.bank_accounts as unknown as PaymentSettings["bank_accounts"]) ?? [],
        cod_enabled: data.cod_enabled,
        cod_surcharge: Number(data.cod_surcharge ?? 0),
        cod_allowed_cities:
          data.cod_allowed_cities ?? DEFAULT_PAYMENT_SETTINGS.cod_allowed_cities,
        azul_enabled: data.azul_enabled,
        manual_transfer_enabled: data.manual_transfer_enabled,
        express_free_threshold_usd: Number(
          data.express_free_threshold_usd ?? DEFAULT_PAYMENT_SETTINGS.express_free_threshold_usd,
        ),
        express_free_note_es:
          data.express_free_note_es ?? DEFAULT_PAYMENT_SETTINGS.express_free_note_es,
        express_free_note_en:
          data.express_free_note_en ?? DEFAULT_PAYMENT_SETTINGS.express_free_note_en,
      });
    };

    const load = async () => {
      let data: any = null;
      let error: unknown = null;
      try {
        const res: any = await supabase
          .from("payment_settings")
          .select("*")
          .eq("id", 1)
          .maybeSingle();
        data = res?.data ?? null;
        error = res?.error ?? null;
      } catch (e) {
        error = e;
      }
      if (!active) return;
      if (error) console.error("payment_settings read error:", error);
      apply(data);
      setLoading(false);
    };

    load();

    // Realtime: when admin saves the singleton row, every open checkout
    // refreshes immediately so customers always see valid methods.
    const channel = supabase
      .channel(`payment_settings_singleton_${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "payment_settings", filter: "id=eq.1" },
        (payload: any) => {
          if (!active) return;
          apply(payload.new ?? payload.record ?? null);
        },
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(channel);
    };
  }, []);

  return { settings, loading };
}